import type { HTMLAttributes, PropsWithChildren, ReactNode } from "react";
import { forwardRef } from "react";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import * as styles from "./Collapsible.styles.ts";
import { useControlled } from "@exonn/hooks";

type CollapsibleProps = PropsWithChildren<
  {
    title: ReactNode;
    collapsed?: boolean;
    defaultCollapsed?: boolean;
    onCollapsedChange?: (collapsed: boolean) => void;
  } & Omit<HTMLAttributes<HTMLDivElement>, "title">
>;

export const Collapsible = forwardRef<HTMLDivElement, CollapsibleProps>(
  (props, ref) => {
    const {
      title,
      children,
      collapsed: collapsedProp,
      defaultCollapsed = true,
      onCollapsedChange,
      ...rest
    } = props;
    const [collapsed, setCollapsed] = useControlled({
      controlled: collapsedProp,
      default: defaultCollapsed,
      state: "isCollapsed",
      name: "Collapsible",
    });
    const toggle = () => {
      setCollapsed(!collapsed);
      onCollapsedChange?.(!collapsed);
    };
    return (
      <div
        css={styles.root({
          collapsed,
        })}
        {...rest}
        ref={ref}
      >
        <button
          onClick={toggle}
          css={styles.trigger({
            collapsed,
          })}
        >
          <div>
            <TextV2 variant={"body1"}>{title}</TextV2>
          </div>
          <div css={styles.arrowIconContainer}>
            <i
              className={"icon-15-arw_dw"}
              css={styles.arrow({
                collapsed,
              })}
            />
          </div>
        </button>
        {!collapsed && (
          <div
            css={styles.content({
              collapsed,
            })}
          >
            {children}
          </div>
        )}
      </div>
    );
  },
);
